<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'meter',
    path: '/meters/:id',
    component: 'Meter',
    params: [
      {
        name: 'id',
        prop: 'meterId',
      },
    ],
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Meter',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    meterId: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const tabManager = ref(null);
  const item = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_8564662037462133_parentTo = computed(() => ({
    name: 'app.meters',
  }));

  const $data = computed(() => ({
    tabManager: tabManager.value,
    item: item.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_8564662037462133_parentTo: b_8564662037462133_parentTo.value,
  }));

  const $$load_tabManager = () => {
    return new $app.value.utils.LazyTabManager();
  };
  const $$load_item = () => {
    return $fn.value.fetch('read_meter', {
      asResource: true,
      parameters: { id: props.meterId },
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'tabManager' },
      { name: 'item' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_8564662037462133_parentTo', nodeId: '8564662037462133' },
    ],
    loaders: { tabManager: $$load_tabManager, item: $$load_item },
    ux: {},
    dataRefs: {
      tabManager,
      item,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_8564662037462133_parentTo,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: [
          item.value.snapshot('serial_number'),
          $fn.value.gettext('Meter'),
        ],
        icon: item.value
          ? $config.value.meterTypes[item.value.meter_type].icon
          : $config.value.meter.icon,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.meterId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('meterId');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_9spha43e1_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_9004123373732307_showModal_sjyd7hgak_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'EntityEvents',
      props: {
        entityId: item.value.id,
      },
    };
  };
  const act_2679161326024595_setData_wwrryneza_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item',
      value: $event.data,
      resolve: false,
      noTouchLastLoaded: true,
      noTakeSnapshot: false,
    };
  };

  const act_7315092382398562_closeModal_9spha43e1 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_9spha43e1_getActionArgs,
    });
  const inst_evh_7315092382398562_close_xydjnsdv2 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_9spha43e1,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_xydjnsdv2],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_3tadqydjo =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_0n6smsvr2 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_3tadqydjo,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_0n6smsvr2],
    event: 'reload',
  });
  const act_4615685607825208_reloadComponentData_2afi7jyga =
    new actionTypes.ReloadComponentDataAction({
      displayName: 'reload',
    });
  const inst_evh_4615685607825208_clickPreventStop_m298w21qu =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_reloadComponentData_2afi7jyga,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_4615685607825208_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4615685607825208_clickPreventStop_m298w21qu],
      event: 'click.prevent.stop',
    });
  const act_2095089031469157_closeComponent_oii0x8ryo =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2095089031469157_success_0by9s5t4a =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_closeComponent_oii0x8ryo,
      event: 'success',
      displayName: 'closeComponent',
    });
  const inst_evh_2095089031469157_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2095089031469157_success_0by9s5t4a],
    event: 'success',
  });
  const act_9004123373732307_showModal_sjyd7hgak =
    new actionTypes.ShowModalAction({
      actionArgs: act_9004123373732307_showModal_sjyd7hgak_getActionArgs,
    });
  const inst_evh_9004123373732307_clickPreventStop_g6i61d4r6 =
    new actionTypes.EventHandlerImpl({
      action: act_9004123373732307_showModal_sjyd7hgak,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_9004123373732307_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_9004123373732307_clickPreventStop_g6i61d4r6],
      event: 'click.prevent.stop',
    });
  const act_2679161326024595_reloadComponentData_k48s1z2as =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_2679161326024595_reload_atlu390or =
    new actionTypes.EventHandlerImpl({
      action: act_2679161326024595_reloadComponentData_k48s1z2as,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_2679161326024595_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2679161326024595_reload_atlu390or],
    event: 'reload',
  });
  const act_2679161326024595_setData_wwrryneza = new actionTypes.SetDataAction({
    actionArgs: act_2679161326024595_setData_wwrryneza_getActionArgs,
  });
  const inst_evh_2679161326024595_saved_uhjycidwi =
    new actionTypes.EventHandlerImpl({
      action: act_2679161326024595_setData_wwrryneza,
      event: 'saved',
      displayName: 'setData',
    });
  const inst_evh_2679161326024595_saved = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2679161326024595_saved_uhjycidwi],
    event: 'saved',
  });
  const act_7987652045662005_reloadComponentData_wnpcowj5c =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7987652045662005_reload_nypq695up =
    new actionTypes.EventHandlerImpl({
      action: act_7987652045662005_reloadComponentData_wnpcowj5c,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7987652045662005_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7987652045662005_reload_nypq695up],
    event: 'reload',
  });
  const act_6806515264385235_reloadComponentData_wjksg4on4 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6806515264385235_reload_l7obga2se =
    new actionTypes.EventHandlerImpl({
      action: act_6806515264385235_reloadComponentData_wjksg4on4,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_6806515264385235_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6806515264385235_reload_l7obga2se],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_close_xydjnsdv2,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_0n6smsvr2,
    inst_evh_7315092382398562_reload,
    inst_evh_4615685607825208_clickPreventStop_m298w21qu,
    reload: inst_evh_4615685607825208_clickPreventStop_m298w21qu,
    inst_evh_4615685607825208_clickPreventStop,
    inst_evh_2095089031469157_success_0by9s5t4a,
    inst_evh_2095089031469157_success,
    inst_evh_9004123373732307_clickPreventStop_g6i61d4r6,
    inst_evh_9004123373732307_clickPreventStop,
    inst_evh_2679161326024595_reload_atlu390or,
    inst_evh_2679161326024595_reload,
    inst_evh_2679161326024595_saved_uhjycidwi,
    inst_evh_2679161326024595_saved,
    inst_evh_7987652045662005_reload_nypq695up,
    inst_evh_7987652045662005_reload,
    inst_evh_6806515264385235_reload_l7obga2se,
    inst_evh_6806515264385235_reload,
  };

  const evh_7315092382398562_close_xydjnsdv2 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_xydjnsdv2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_0n6smsvr2 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_0n6smsvr2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_clickPreventStop_m298w21qu = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4615685607825208_clickPreventStop_m298w21qu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_success_0by9s5t4a = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_success_0by9s5t4a.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_success = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_9004123373732307_clickPreventStop_g6i61d4r6 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_9004123373732307_clickPreventStop_g6i61d4r6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_9004123373732307_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_9004123373732307_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2679161326024595_reload_atlu390or = async (event, scope) => {
    return await actions.inst_evh_2679161326024595_reload_atlu390or.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2679161326024595_reload = async (event, scope) => {
    return await actions.inst_evh_2679161326024595_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2679161326024595_saved_uhjycidwi = async (event, scope) => {
    return await actions.inst_evh_2679161326024595_saved_uhjycidwi.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2679161326024595_saved = async (event, scope) => {
    return await actions.inst_evh_2679161326024595_saved.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7987652045662005_reload_nypq695up = async (event, scope) => {
    return await actions.inst_evh_7987652045662005_reload_nypq695up.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7987652045662005_reload = async (event, scope) => {
    return await actions.inst_evh_7987652045662005_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6806515264385235_reload_l7obga2se = async (event, scope) => {
    return await actions.inst_evh_6806515264385235_reload_l7obga2se.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6806515264385235_reload = async (event, scope) => {
    return await actions.inst_evh_6806515264385235_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Meter"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="page--v2 d-flex flex-column" v-if="loaded">
      <MaterialTabs
        :tabManager="tabManager"
        :title="item.snapshot('serial_number')"
        :parentText="gettext('Meters')"
        :parentTo="b_8564662037462133_parentTo"
        class="h-100"
      >
        <template #actions>
          <ItpButton
            variant="light"
            icon="fa-sync-alt"
            :text="gettext('Reload')"
            size="sm"
            :spinning="actions.reload.isRunning"
            @click.prevent.stop="
              evh_4615685607825208_clickPreventStop($event, {})
            "
          >
          </ItpButton>
          <BtnDeleteResource
            :resource="item"
            @success="evh_2095089031469157_success($event, {})"
          >
          </BtnDeleteResource>
          <ItpButton
            variant="default"
            icon="material-symbols:history"
            :text="pgettext('Invoice', 'Invoice history')"
            size="sm"
            @click.prevent.stop="
              evh_9004123373732307_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </template>
        <BTab :title="gettext('General')" class="container-lg float-start">
          <ItpRow>
            <ItpCol :w="6">
              <UcMeterGeneralPanel
                :meter="item"
                :reload="lastLoaded"
                class="pb-4"
                @reload="evh_2679161326024595_reload($event, {})"
                @saved="evh_2679161326024595_saved($event, {})"
              >
              </UcMeterGeneralPanel>
              <UcRentalServices
                :meter="item"
                :reload="lastLoaded"
                v-if="item"
                @reload="evh_7987652045662005_reload($event, {})"
              >
              </UcRentalServices>
            </ItpCol>
            <ItpCol :w="6">
              <UcCommentsList :parent="item" :reload="lastLoaded" class="pb-4">
              </UcCommentsList>
              <UcAttachmentsList
                :parent="item"
                :reload="lastLoaded"
                class="pb-4"
              >
              </UcAttachmentsList>
            </ItpCol>
          </ItpRow>
        </BTab>
        <BTab
          :title="pgettext('Meter', 'Readings')"
          class="container-fluid h-100"
        >
          <UcMeterReadings
            :meter="item"
            :reload="lastLoaded"
            class="h-100"
            v-if="tabManager.visited(1)"
          >
          </UcMeterReadings>
        </BTab>
        <BTab
          :title="pgettext('Meter', 'Consumptions')"
          class="container-fluid h-100"
          @reload="evh_6806515264385235_reload($event, {})"
        >
          <UcMeterConsumptions
            :meter="item"
            :reload="lastLoaded"
            class="h-100"
            v-if="tabManager.visited(2)"
          >
          </UcMeterConsumptions>
        </BTab>
        <BTab
          :title="pgettext('Meter', 'Auto Readings')"
          class="container-fluid h-100"
        >
          <UcMeterCharts
            :meter="item"
            :reload="lastLoaded"
            class="h-100"
            v-if="tabManager.visited(3)"
          >
          </UcMeterCharts>
        </BTab>
        <BTab
          :title="pgettext('Meter', 'Invoices')"
          class="container-fluid h-100"
        >
          <UcMeterInvoices
            :meter="item"
            :reload="lastLoaded"
            v-if="tabManager.visited(4)"
          >
          </UcMeterInvoices>
        </BTab>
      </MaterialTabs>
    </div>
  </ItpPage>
</template>
